import { yellow } from '@mui/material/colors';
import { darken, lighten } from '@mui/material/styles';

import { mediaQuery } from 'constants/ui';
import { checkIsDarkTheme, checkIsLightTheme } from 'utils/theme';

import {
  COLOR_LIGHTEN_FACTOR,
  FORM_BUTTONS_TOP_OFFSET,
  PERCENTAGE,
  SeverityColorsType,
  SPACING_LG,
  SPACING_SM,
  YELLOW_SHADE_DARK,
  YELLOW_SHADE_LIGHT,
} from './LoanApplication.constants';

export const getColumnsWidthStyles = (theme, { columns } = {}) => {
  const { breakpoints } = theme;

  return {
    wrapper: {
      alignItems: 'center',
      width: '100%',

      '& > *': {
        width: columns ? `${PERCENTAGE / columns}%` : '100%',
        padding: '0 5px',
      },

      [breakpoints.down('sm')]: {
        alignItems: 'flex-start',
        width: '50%',

        '& > *': {
          width: '100%',
          minHeight: 30,
          display: 'inline-block',
          padding: '5px',
        },
      },
    },
  };
};

export const getSharedStyles = theme => {
  const { palette } = theme;
  const isLightTheme = checkIsLightTheme(palette.type);
  const isDarkTheme = checkIsDarkTheme(palette.type);
  const colorCustomizeMethod = isDarkTheme ? lighten : darken;

  return {
    label: {
      color: palette.secondary.main,
      fontWeight: 700,
      marginRight: '5px',
    },
    value: {
      color: isLightTheme ? palette.common.black : palette.common.white,
      fontWeight: 300,

      [`&[data-color="${SeverityColorsType.RED}"]`]: {
        color: palette.error[isLightTheme ? 'dark' : 'A700'],
      },
      [`&[data-color="${SeverityColorsType.YELLOW}"]`]: {
        color: yellow[isLightTheme ? YELLOW_SHADE_LIGHT : YELLOW_SHADE_DARK],
      },

      '&[data-type="boolean"]': {
        display: 'inline-block',
        width: 30,
        height: 8,
        borderRadius: 30,
        color: 'transparent',

        '&[data-value="false"]': {
          backgroundColor: palette.error[isLightTheme ? 'dark' : 'A700'],
        },

        '&[data-value="true"]': {
          backgroundColor: palette.success[isLightTheme ? 'dark' : '500'],
        },
      },
    },
    evenGroup: {
      background: colorCustomizeMethod(palette.background.default, COLOR_LIGHTEN_FACTOR),
    },
  };
};

export const getSharedFormStyles = (theme, { isNewForm } = {}) => {
  const { palette } = theme;
  const isLightTheme = checkIsLightTheme(palette.type);

  return {
    addNewButton: {
      color: palette.primary.contrastText,
      backgroundColor: palette.secondary.main,
      borderRadius: 9999,
      textTransform: 'capitalize',
    },
    submitButton: {
      minWidth: 100,
      borderRadius: 9999,
    },
    cancelButton: {
      minWidth: 120,
      backgroundColor: 'transparent',
      borderRadius: 9999,
      color: palette.common[isLightTheme ? 'black' : 'white'],
      border: `1px ${palette.grey[400]} solid`,
      marginLeft: '16px',
      marginRight: isNewForm ? `${SPACING_LG}px` : `${SPACING_SM}px`,
      '&:hover': {
        color: palette.common.black,
      },
    },
    buttonsWrapper: {
      position: 'absolute',
      right: 0,
      top: isNewForm ? SPACING_SM : FORM_BUTTONS_TOP_OFFSET,
    },
    container: {
      position: 'relative',
      width: '100%',
      padding: isNewForm ? '8px 16px 0px' : null,
    },
    root: {
      '& .generator__form-group__title': {
        color: palette.secondary.main,
        marginLeft: '8px',
        marginBottom: '12px',
      },
    },
    textHighlight: {
      color: palette.primary.main,
    },
    textFieldHighlight: {
      '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline, & fieldset, &:hover fieldset, &.Mui-focused fieldset':
        {
          borderColor: `${palette.primary.main} !important`,
        },
    },
  };
};

export const getSharedAccordionStyles = theme => {
  const { palette, breakpoints } = theme;
  const isDarkTheme = checkIsDarkTheme(palette.type);

  return {
    accordionTop: {
      display: 'flex',
      alignItems: 'center',
      minHeight: 40,
      backgroundColor: isDarkTheme ? palette.grey.A400 : palette.common.white,

      [breakpoints.down('sm')]: {
        alignItems: 'flex-start',
      },

      '& [data-is-field-changed="true"]': {
        color: palette.primary.main,
        '& .MuiCheckbox-root, & .MuiRadio-root': {
          color: palette.primary.main,
        },
      },
    },
    accordionWrapper: {
      '& > :not(:last-child)': {
        borderBottom: `1px solid ${palette.grey[isDarkTheme ? '500' : '200']}`,
      },

      '.MuiAccordion-root.Mui-expanded': {
        marginTop: 0,
      },
    },
    accordion: {
      '& > form': {
        backgroundColor: isDarkTheme ? palette.background.paper : palette.common.white,
        padding: '20px 30px',
        borderRadius: '20px',
      },
    },
    root: {
      '& .MuiTypography-root': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      '& .MuiAccordionSummary-expandIcon': {
        position: 'absolute',
        right: '12px',
      },
      '& .MuiAccordion-root': {
        position: 'relative',
      },
      '& .MuiAccordionSummary-root': {
        padding: '0 4px 0 24px',
      },
      '& .MuiAccordionSummary-content': {
        width: 100,
      },
    },
    accordionNoShadow: {
      '& > :not(:last-child)': {
        borderBottom: `1px solid ${palette.grey[isDarkTheme ? '500' : '200']}`,
      },
    },
  };
};

export const mortgageRadioStyles = {
  marginBottom: '24px',
};

export const mortgageCheckboxGroupStyles = {
  gridTemplateColumns: `repeat(4, 1fr)`,
  marginTop: '24px',

  [mediaQuery.mobile]: {
    gridTemplateColumns: '1fr',
  },
  [mediaQuery.tablet]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },

  '& .checkbox ': {
    marginLeft: '0',
  },
};

export const checklistRecalculationIconAnimationStyles = {
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  animation: 'spin 1s linear infinite',
};
